import {
  buildEmailValues,
  computeLadder,
  jobAddress,
  ratio,
  sumBy,
  type EmailTemplate,
  type Job,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import {
  fetchCompanySettings,
  fetchLadderInputs,
  type CompanySettingsRow,
} from "../../lib/queries";
import { supabase, unwrap } from "../../lib/supabase";
import { useSession } from "../../session";
import { ComposeEmail } from "../email/ComposeEmail";
import { Conversations } from "../email/Conversation";
import { useGmailAccount, useGmailSenders } from "../email/useGmailAccount";

/**
 * The Emails card on a job.
 *
 * The templates already existed under Tools → Emails and had nowhere to go;
 * this is where one gets pointed at a customer and sent. Everything a merge
 * field can resolve to is loaded here — the job, the contact, the company's own
 * details, and the totals off the active estimate — because a template that
 * greets somebody by name and then says `{{estimate.total}}` is worse than one
 * that never mentioned the money.
 */

interface SentEmail {
  body: string | null;
  occurred_at: string;
  direction: string | null;
  who: { full_name: string | null } | null;
}

export function EmailPanel({ job }: { job: Job }) {
  const { displayName, profile } = useSession();
  const { account } = useGmailAccount();
  const { senders, own: ownSender } = useGmailSenders();
  const [composing, setComposing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const templates = useAsync(
    async () =>
      unwrap<EmailTemplate[]>(
        await supabase
          .from("email_templates")
          .select("*")
          .eq("context", "job")
          .eq("active", true)
          .order("sort_order")
          .order("name"),
      ),
    [],
  );

  const company = useAsync(fetchCompanySettings, []);

  const sent = useAsync(
    async () =>
      unwrap<SentEmail[]>(
        await supabase
          .from("activities")
          .select(
            "body,occurred_at,direction, who:profiles!activities_created_by_fkey(full_name)",
          )
          .eq("job_id", job.id)
          .eq("type", "email")
          .order("occurred_at", { ascending: false }),
      ),
    [job.id],
  );

  /** The active estimate's totals, for the `{{estimate.*}}` fields. */
  const totals = useAsync(async () => {
    const byJob = await fetchLadderInputs([job.id]);
    const inputs = byJob.get(job.id) ?? [];
    if (inputs.length === 0) return null;
    const ladders = inputs.map((input) =>
      computeLadder({ ...input, jobType: job.job_type, place: job }),
    );
    // A job selling two options is sold as both, so the letter quotes both —
    // `multi_option`, the same rule the contract total follows.
    const total = sumBy(ladders, (l) => l.grandTotal);
    const grossProfit = sumBy(ladders, (l) => l.grossProfit);
    return {
      total,
      grossProfit,
      materials: sumBy(ladders, (l) => l.materialCost),
      labor: sumBy(ladders, (l) => l.laborCost),
      margin: ratio(grossProfit, total),
    };
  }, [job.id, job.job_type]);

  const contact = job.contact ?? null;

  const values = buildEmailValues({
    job,
    contact,
    people: {
      rep: job.rep?.full_name ?? null,
      createdBy: job.creator?.full_name ?? null,
    },
    totals: totals.data ?? null,
    company: (company.data as CompanySettingsRow | null) ?? null,
    sender: {
      name: displayName,
      email: account?.email_address ?? profile?.email ?? null,
    },
    jobUrl: `${window.location.origin}/#job=${job.id}`,
  });

  return (
    <div className="block">
      <ErrorNote error={error ?? templates.error ?? sent.error} />

      <div className="rrow">
        <span className="k">{t("Send as")}</span>
        <span className="v">
          {account ? (
            account.email_address
          ) : (
            <span className="badge draft">{t("Gmail not connected")}</span>
          )}
        </span>
      </div>

      <button
        className="addrow"
        style={{ marginTop: 10 }}
        onClick={() => {
          if (!contact?.email) {
            setError(
              t(
                "This customer has no email address. Add one under Overview and try again.",
              ),
            );
            return;
          }
          setError(null);
          setComposing(true);
        }}
      >
        {t("Write an email")}
      </button>

      <Conversations job={job} />

      <h4 style={{ margin: "22px 0 8px" }}>{t("History")}</h4>
      {sent.loading ? (
        <div className="na-hint">{t("Loading…")}</div>
      ) : (sent.data ?? []).length === 0 ? (
        <div className="na-hint">
          {t("Nothing has been emailed about this job.")}
        </div>
      ) : (
        (sent.data ?? []).map((row, index) => (
          <div className="rrow" key={`${row.occurred_at}-${index}`}>
            <span className="k">
              {new Date(row.occurred_at).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}
            </span>
            <span className="v">
              {row.body}
              {row.who?.full_name ? ` · ${row.who.full_name}` : ""}
            </span>
          </div>
        ))
      )}

      {composing && (
        <ComposeEmail
          title={t("Email {name}", {
            name: contact?.full_name ?? t("the customer"),
          })}
          subtitle={jobAddress(job)}
          to={contact?.email ?? ""}
          subject=""
          body=""
          templates={templates.data ?? []}
          values={values}
          contact={contact}
          contactId={job.contact_id}
          jobId={job.id}
          connectedAs={account?.email_address ?? null}
          senders={senders}
          ownSender={ownSender}
          onClose={() => setComposing(false)}
          onSent={async () => {
            await sent.reload();
          }}
        />
      )}
    </div>
  );
}
